import { useCallback, useMemo, useRef } from 'react'
import { isToday, isYesterday } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { createColumnHelper } from '@tanstack/react-table'
import clsx from 'clsx'

import { BusStop, FilterType, Log, RouteSelectionMode, RouteBusStops } from '@/types/logs'
import { COMBINED_LOGS_COLUMNS } from '@/modules/CombinedLogs/constant'
import { CommonHeader, SortHeader } from '@/ui/Table'
import { Code } from '@/modules/CombinedLogs/ui/components/information/Code'
import { Location } from '@/modules/CombinedLogs/ui/components/information/Location'
import { Trip } from '@/modules/CombinedLogs/ui/components/information/Trip'
import { AuthorTooltip } from '@/modules/CombinedLogs/ui/components/TableForm/tooltips/AuthorTooltip'
import { FlagTooltip } from '../ui/components/FlagTooltip'
import { DeadlineHeader } from '@/pages/UnfinishedRecords/ui/components/performanceManager/DeadlineHeader'
import { PrimitiveDefaultValue } from '@/pages/UnfinishedRecords/ui/components/PrimitiveDefaultValue'
import { DefaultValue } from '@/pages/UnfinishedRecords/ui/components/DefaultValue'
import { UPDATED_RECORDS_LABEL_MAP } from '@/pages/UnfinishedRecords/constant'
import { useCachedGetCodeName } from '@/modules/CombinedLogs/hooks/useCachedGetCodeName'
import { useManageLogOriginalRoute } from '@/modules/CombinedLogs/hooks/useManageLogOriginalRoute'
import { useGetCodes } from '@/modules/CombinedLogs/api/useGetCodes'
import { sortLogsBy } from '@/modules/CombinedLogs/helpers/sortLogsBy'
import { filterLogsBy } from '@/modules/CombinedLogs/helpers/filterLogsBy'
import { sortLogsByTime } from '@/modules/CombinedLogs/helpers/sortLogsByTime'
import { formatCodeFilterValues } from '@/modules/CombinedLogs/helpers/formatCodeFilterValues'
import { getNameInitials } from '@/helpers/getNameInitials'

import { FlagStateIcon } from '@/modules/CombinedLogs/ui/components/information/FlagStateIcon'
import LockIcon from '@/modules/CombinedLogs/assets/lock.svg'
import UnlockIcon from '@/modules/CombinedLogs/assets/unlock.svg'
import IndicatorIcon from '@/pages/UnfinishedRecords/assets/indicator.svg'

import styles from './useFlaggedRecordsColumns.module.scss'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import FlagIcon from '../assets/flag.svg'
import { LockHeader } from '@/ui/Table/ui/components/LockHeader'
import { LinkToLog } from '@/pages/UnfinishedRecords/ui/components/LinkToLog'

const FLAGGED_COLUMNS = {
    Unfinished: 'unfinished',
    Flag: 'flag',
    Locked: 'locked',
}

const columnHelper = createColumnHelper<Log>()

interface BusStopsData {
    getRouteBusStops: (route?: string) => RouteBusStops | undefined
    allStops: BusStop[]
}

export const useFlaggedRecordsColumns = (isDataLoading: boolean, busStops: BusStopsData, actionCallback?: (log: Log) => void) => {
    const { role } = useAuthStore()
    const emptyCodesRef = useRef([])
    const { data: codes = emptyCodesRef.current } = useGetCodes()
    const getCodeName = useCachedGetCodeName()
    const { getElementAttributes } = useManageLogOriginalRoute(isDataLoading)

    const nameInitialsCache = useRef(new Map<string, string>())
    const getNameInitialsMemoized = useCallback((value: string) => {
        if (nameInitialsCache.current.has(value)) {
            return nameInitialsCache.current.get(value)
        }
        const initials = getNameInitials(value)
        nameInitialsCache.current.set(value, initials)
        return initials
    }, [])

    return useMemo(
        () => [
            columnHelper.accessor('code', {
                id: COMBINED_LOGS_COLUMNS.Code,
                header: (context) => (
                    <CommonHeader
                        label="Code"
                        buttonLabel="Sort by code"
                        searchPlaceholder="Type to search for code"
                        {...context}
                    />
                ),
                cell: (info) => <Code {...info} getCodeName={getCodeName} />,
                filterFn: filterLogsBy((log) => !log.code),
                meta: {
                    isPlaceholder: true,
                    placeholder: 'Code',
                    alignCenter: true,
                    width: 'var(--size-128)',
                    formatFilterValues: (values) => formatCodeFilterValues(values, FilterType.All, codes),
                },
                enableSorting: true,
            }),
            columnHelper.accessor('reference', {
                id: COMBINED_LOGS_COLUMNS.Reference,
                header: 'Reference',
                cell: (info) => <DefaultValue {...info} />,
                enableSorting: false,
                meta: {
                    alignCenter: true,
                    width: '7.142rem',
                },
            }),
            columnHelper.accessor((row) => row.routes, {
                id: COMBINED_LOGS_COLUMNS.Routes,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by route"
                        searchPlaceholder="Type to search for route"
                        label="Route"
                    />
                ),
                cell: (info) =>
                    info.row.original.routeSelectionMode === RouteSelectionMode.None ? (
                        <>-</>
                    ) : (
                        <PrimitiveDefaultValue value={info.getValue()} updated={false} />
                    ),
                meta: {
                    alignCenter: true,
                    width: '7.142rem',
                },
                filterFn: filterLogsBy((log) => log.routeSelectionMode === RouteSelectionMode.None),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Routes),
            }),
            columnHelper.accessor('trip', {
                id: COMBINED_LOGS_COLUMNS.Trip,
                header: 'Trip',
                cell: (info) => (
                    <Trip {...info} isDataLoading={isDataLoading} getElementAttributes={getElementAttributes} />
                ),
                meta: { alignCenter: true },
            }),
            columnHelper.accessor('run', {
                id: COMBINED_LOGS_COLUMNS.Run,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by run"
                        searchPlaceholder="Type to search for run"
                        label="Run"
                    />
                ),
                cell: (info) => <PrimitiveDefaultValue value={info.getValue()} updated={false} />,
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Run, (log) => !log.run),
                filterFn: filterLogsBy((log) => !!log.run),
                meta: { alignCenter: true },
            }),
            columnHelper.accessor('duty', {
                id: COMBINED_LOGS_COLUMNS.Duty,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by duty"
                        searchPlaceholder="Type to search for duty"
                        label="Duty"
                    />
                ),
                cell: (info) => <PrimitiveDefaultValue value={info.getValue()} updated={false} />,
                filterFn: filterLogsBy((log) => !log.duty.length),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Duty, (log) => !log.duty?.length),
                meta: { alignCenter: true },
            }),
            columnHelper.accessor('fleet', {
                id: COMBINED_LOGS_COLUMNS.Fleet,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by fleet"
                        searchPlaceholder="Type to search for fleet"
                        label="Fleet"
                    />
                ),
                cell: (info) => <PrimitiveDefaultValue value={info.getValue()} updated={false} />,
                filterFn: filterLogsBy((log) => !log.fleet?.length),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Fleet, (log) => !log.fleet?.length),
                meta: { alignCenter: true },
            }),
            columnHelper.accessor(
                (row) => ({
                    locationFrom: row.locationFrom,
                    locationTo: row.locationTo,
                    locationType: row.locationType,
                    direction: row.direction,
                }),
                {
                    id: COMBINED_LOGS_COLUMNS.Location,
                    header: 'Location',
                    cell: (info) => (
                        <Location
                            {...info}
                            getRouteBusStops={busStops.getRouteBusStops}
                            allStopsData={busStops.allStops}
                        />
                    ),
                    meta: { width: '100%' },
                }
            ),
            columnHelper.accessor((row) => row.descriptionInfo.description, {
                id: COMBINED_LOGS_COLUMNS.Description,
                header: 'Description',
                cell: (info) => <PrimitiveDefaultValue value={info.getValue()} updated={false} />,
                meta: { width: '100%' },
            }),
            ...(role === Role.PerformanceManager
                ? [
                      columnHelper.accessor('delayInMinutes', {
                          id: COMBINED_LOGS_COLUMNS.Delay,
                          header: 'Delay',
                          cell: (info) => {
                              const value = info.getValue()
                              if (!value) return <>-</>
                              return <span className={styles.greyCell}>{`${value}${!isNaN(value) ? ' min' : ''}`}</span>
                          },
                          meta: { alignCenter: true },
                      }),
                  ]
                : []),
            columnHelper.accessor('mileage', {
                id: COMBINED_LOGS_COLUMNS.Milage,
                header: 'Mileage',
                cell: (info) => <PrimitiveDefaultValue value={info.getValue()} updated={false} />,
                meta: { alignCenter: true },
            }),
            ...(role === Role.PerformanceManager
                ? [
                      columnHelper.accessor('workstation', {
                          id: 'workstation',
                          header: (context) => (
                              <CommonHeader
                                  {...context}
                                  buttonLabel="Sort by workstation"
                                  searchPlaceholder="Type to search for workstation"
                                  label="Workstn"
                              />
                          ),
                          cell: (info) => <PrimitiveDefaultValue value={info.getValue()} updated={false} />,
                          meta: { alignCenter: true, width: '6.428rem' },
                      }),
                  ]
                : []),
            columnHelper.accessor('author', {
                id: COMBINED_LOGS_COLUMNS.Author,
                header: 'Author',
                cell: (info) => (
                    <AuthorTooltip author={info.getValue() || '-'}>
                        <span className={styles.greyCell}>
                            {info.getValue() ? getNameInitialsMemoized(info.getValue()) : '-'}
                        </span>
                    </AuthorTooltip>
                ),
                meta: { alignCenter: true },
            }),
            columnHelper.accessor('timestamp', {
                id: COMBINED_LOGS_COLUMNS.Time,
                header: (context) => <SortHeader {...context} buttonLabel="Sort by time" label="Time" />,
                cell: (info) => {
                    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
                    return (
                        <span className={styles.greyCell}>
                            {formatInTimeZone(info.getValue(), timezone, 'HH:mm dd/MM/yy')}
                        </span>
                    )
                },
                sortingFn: (rowA, rowB) => -sortLogsByTime(rowA.original, rowB.original),
                meta: { alignCenter: true, width: '7.142rem' },
            }),
            ...(role === Role.PerformanceManager
                ? [
                      columnHelper.accessor('timestamp', {
                          id: COMBINED_LOGS_COLUMNS.Deadline,
                          header: (context) => <DeadlineHeader {...context} />,
                          cell: (info) => {
                              const formattedTime = formatInTimeZone(info.getValue(), 'UTC', "yyyy-MM-dd'T'HH:mm:ss")
                              const showAsToday = isToday(formattedTime) || isYesterday(formattedTime)
                              return (
                                  <div
                                      className={clsx({ [styles.overdue]: !showAsToday, [styles.today]: showAsToday })}
                                  >
                                      <span>{showAsToday ? 'Today' : 'Overdue'}</span>
                                      {info.row.original.isUpdatedAfterUnfinished && <IndicatorIcon />}
                                  </div>
                              )
                          },
                          sortingFn: (rowA, rowB) => -sortLogsByTime(rowA.original, rowB.original),
                          meta: { alignCenter: true, width: '7.142rem' },
                      }),
                  ]
                : []),
            columnHelper.display({
                id: FLAGGED_COLUMNS.Unfinished,
                header: 'Unfinished',
                cell: (info) => {
                    const { unfinishedType } = info.row.original
                    if (!unfinishedType) return <>-</>
                    return <span className={styles.unfinishedLabel}>{UPDATED_RECORDS_LABEL_MAP[unfinishedType]}</span>
                },
                meta: { alignCenter: true, width: '9rem' },
            }),
            columnHelper.display({
                id: FLAGGED_COLUMNS.Flag,
                header: () => (
                    <span className={styles.iconHeader}>
                        <FlagIcon />
                    </span>
                ),
                cell: (info) => {
                    const { id, unfinishedFlagMetadata } = info.row.original
                    const icon = <FlagStateIcon metadata={unfinishedFlagMetadata} />
                    if (!unfinishedFlagMetadata?.comment) return icon
                    return (
                        <FlagTooltip logId={id} metadata={unfinishedFlagMetadata} role={role}>
                            {icon}
                        </FlagTooltip>
                    )
                },
                meta: { alignCenter: true, width: '4rem' },
            }),
            columnHelper.accessor('isLocked', {
                id: FLAGGED_COLUMNS.Locked,
                header: (context) => <LockHeader {...context} />,
                cell: (info) => (info.getValue() ? <LockIcon /> : <UnlockIcon />),
                enableSorting: false,
                meta: { alignCenter: true, width: '5rem' },
            }),
            ...(actionCallback
                ? [
                      columnHelper.display({
                          id: 'linkToLog',
                          cell: (info) => (
                              <LinkToLog {...info} handleArrowClick={actionCallback} />
                          ),
                          meta: { alignCenter: true, width: '2.5rem' },
                      }),
                  ]
                : []),
        ],
        [isDataLoading, getCodeName, getElementAttributes, getNameInitialsMemoized, codes, busStops, role, actionCallback]
    )
}
